<script setup>
import BaseButton from "@/app/components/ui/BaseButton.vue"
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useRoute } from "@/app/router"
import { useCategoryTranslation } from "@/casino/composables/useCategoryTranslation"

import { ref } from "vue"
import { useRouter } from "vue-router"
import CasinoPanelFilters from "./CasinoPanelFilters.vue"

defineProps({
  categories: {
    type: Array,
    required: true,
  },
  searchValue: {
    type: String,
    default: "",
  },
})

const emit = defineEmits(["search", "sort", "providers"])

const route = useRoute()
const router = useRouter()
const { getCategoryName } = useCategoryTranslation()

function handleCategory(slug) {
  if (slug === "home") {
    router.push({ name: "casino.lobby" })
    return
  }

  if (slug === "providers") {
    router.push({ name: "casino.providers" })
    return
  }

  router.push({ name: "casino.category", params: { slug } })
}

function isSelected(category) {
  if (route.name === "casino.lobby" && category.slug === "home") {
    return true
  }

  if (route.name === "casino.providers" && category.slug === "providers") {
    return true
  }

  return route.params.slug === category.slug
}

let isDown = false
let startX
let scrollLeft

const categoriesRef = ref(null)

function handleMouseDown(e) {
  isDown = true
  startX = e.pageX - categoriesRef.value.offsetLeft
  scrollLeft = categoriesRef.value.scrollLeft
}

function handleMouseLeave() {
  isDown = false
}

function handleMouseUp() {
  isDown = false
}

function handleMouseMove(e) {
  if (!isDown)
    return
  e.preventDefault()
  const x = e.pageX - categoriesRef.value.offsetLeft
  const walk = (x - startX) * 1
  categoriesRef.value.scrollLeft = scrollLeft - walk
}

function handleSearch(category) {
  emit("search", "")
  handleCategory(category.slug)
}
</script>

<template>
  <section class="panel-wrapper">
    <div
      ref="categoriesRef"
      class="categories"
      @mousedown="handleMouseDown"
      @mouseleave="handleMouseLeave"
      @mouseup="handleMouseUp"
      @mousemove="handleMouseMove"
    >
      <BaseButton
        v-for="category in categories"
        :key="category.id"
        :slug="category.slug"
        :games_number="category.games_number"
        class="category text"
        :class="{ active: isSelected(category) }"
        @click="handleSearch(category)"
      >
        <BaseIcon
          v-if="category.icon"
          :name="category.icon"
          :filter="
            isSelected(category)
              ? 'drop-shadow(0px 0px 8px rgba(72, 255, 145, 0.5))'
              : ''
          "
          size="20"
        />
        <img
          v-else-if="category.image_url"
          :src="category.image_url"
          width="20px"
          height="20px"
          :style="{
            filter: isSelected(category)
              ? 'drop-shadow(0px 0px 8px rgba(72, 255, 145, 0.5))'
              : '',
          }"
        >
        <BaseIcon
          v-else
          name="poker-cards"
          :filter="
            isSelected(category)
              ? 'drop-shadow(0px 0px 8px rgba(72, 255, 145, 0.5))'
              : ''
          "
          size="20"
        />
        <span class="category-name">{{ getCategoryName(category) }}</span>
      </BaseButton>
    </div>
  </section>
  <CasinoPanelFilters
    :search-value="searchValue"
    @search="emit('search', $event)"
    @sort="emit('sort', $event)"
    @providers="emit('providers', $event)"
  />
</template>

<style lang="scss" scoped>
.categories {
  &::-webkit-scrollbar {
    height: 0px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  cursor: grab;
  &.active {
    cursor: grabbing;
  }
}

.panel-wrapper {
  background-color: var(--be-big-card-bg);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;

  .mobile &,
  .tablet & {
    display: block;
  }

  .categories {
    display: flex;
    padding: 8px 10px 8px;
    overflow-y: hidden;
    width: 100%;
    border-radius: 10px;

    .category {
      user-select: none;
      height: 63px;
      gap: 9px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-width: 137px;
      color: var(--be-disable-state);
      margin: 0 3px;
      font-weight: 600;
      font-size: 11px;
      letter-spacing: 1px;
      padding: 0px;
      font-family: Rubik;
      .mobile &,
      .tablet & {
        font-size: 10px;
      }

      .category-name {
        text-transform: none;
        user-select: none;
        display: flex;
        justify-content: center;
        align-items: center;
        line-height: 1.2;
        font-size: 14px;
        font-family: Rubik;
        font-weight: 400;
      }

      img {
        max-width: 24px;
        height: 24px;
      }
    }

    @media screen and (max-width: 550px) {
      .category {
        width: auto;
        padding: 0 10px;
        gap: 7px;
        min-width: max-content;
      }
    }

    .active {
      opacity: 1;
      background: var(--be-small-card-bg);
      color: var(--be-primary-btn);
      border-radius: 6px;

      .category-name {
        filter: drop-shadow(0px 0px 8px rgba(72, 255, 145, 0.5));
      }
    }
  }

  @media screen and (max-width: 550px) {
    .categories {
      padding: 5px 2px 4px;
      margin-right: 0px;
      margin-top: 20px;
    }
  }
}
</style>
